import React from 'react';

/**
 * Standard form input used across admin and public forms. Owning components
 * pass a controlled `value` + `onChange` pair and an optional `testId`.
 *
 * Keeping a single implementation here removes the duplicated `Field` helpers
 * that previously lived inline in PaymentPage / AdminPostsPage /
 * AdminPaymentLinksPage / ContactSection.
 */
export const FormField = ({
    label,
    value,
    onChange,
    type = 'text',
    placeholder,
    required = false,
    testId,
    autoComplete,
    requiredMark = false,
    inputClassName = '',
    labelClassName = 'meta-eyebrow text-la-text3',
}) => (
    <div>
        <label className={labelClassName}>
            {label}
            {requiredMark && <span className="text-la-gold"> *</span>}
        </label>
        <input
            type={type}
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            required={required}
            autoComplete={autoComplete}
            data-testid={testId}
            className={`mt-3 w-full rounded-la-sm bg-white/[0.02] border border-white/10 px-4 py-3 text-[14px] text-la-text placeholder:text-la-text3 focus:outline-none focus:border-la-gold/40 ${inputClassName}`}
        />
    </div>
);

/**
 * Compact variant used inside the admin dialogs (denser typography +
 * lighter chrome). Keeps a separate visual rhythm from the public forms.
 */
export const AdminFormField = ({
    label,
    value,
    onChange,
    type = 'text',
    required = false,
    testId,
}) => (
    <div>
        <label className="meta-eyebrow">{label}</label>
        <input
            type={type}
            value={value}
            onChange={onChange}
            required={required}
            data-testid={testId}
            className="mt-2 w-full rounded-la-sm bg-white/[0.04] border border-white/10 px-3 py-2 text-sm"
        />
    </div>
);

/**
 * Currency input used by the payment + payment-link forms. The £ prefix is
 * rendered in-line and the value remains controlled by the parent.
 */
export const CurrencyField = ({
    label,
    value,
    onChange,
    min = '1.01',
    max = '250000',
    required = false,
    testId,
    helper,
    requiredMark = false,
}) => (
    <div>
        <label className="meta-eyebrow text-la-text3">
            {label}
            {requiredMark && <span className="text-la-gold"> *</span>}
        </label>
        <div className="mt-3 flex items-center gap-2 rounded-la-sm bg-white/[0.02] border border-white/10 px-4 py-3 focus-within:border-la-gold/40">
            <span className="text-la-text2">£</span>
            <input
                type="number"
                inputMode="decimal"
                step="0.01"
                min={min}
                max={max}
                value={value}
                onChange={onChange}
                required={required}
                data-testid={testId}
                className="w-full bg-transparent text-[15px] text-la-text placeholder:text-la-text3 focus:outline-none"
                placeholder="0.00"
            />
        </div>
        {helper && <p className="mt-2 text-[11px] text-la-text3">{helper}</p>}
    </div>
);

export default FormField;
